import type { GooglePlaceSuggestion } from "./types";
import { BUSINESS_LOCATION_BIAS_RADIUS_METERS } from "./locationBias";

export { BUSINESS_LOCATION_BIAS_RADIUS_METERS };
const LEGAL_SUFFIX_RE =
  /\b(?:l\.?\s*l\.?\s*c\.?|llc|inc\.?|incorporated|corp\.?|corporation|co\.?|company|ltd\.?|limited|pllc|pc|p\.c\.)\s*$/i;

/** Generic tokens that drown distinctive business names in Places ranking. */
const BUSINESS_STOPWORDS = new Set([
  "llc",
  "inc",
  "incorporated",
  "corp",
  "corporation",
  "co",
  "company",
  "ltd",
  "limited",
  "pllc",
  "pc",
  "the",
  "and",
  "of",
  "a",
  "an",
  "solutions",
  "solution",
  "services",
  "service",
  "group",
  "groups",
  "enterprises",
  "enterprise",
  "associates",
  "association",
  "construction",
  "contracting",
  "contractors",
  "contractor",
  "roofing",
  "remodeling",
  "remodel",
  "home",
  "improvement",
  "improvements",
  "general",
  "pros",
  "pro",
  "best",
  "quality",
  "american",
]);

export const stripLegalBusinessSuffixes = (input: string): string => {
  let value = input.trim().replace(/\s+/g, " ");
  // Strip repeatedly in case of "Foo LLC Inc"
  for (let i = 0; i < 3; i++) {
    const next = value.replace(LEGAL_SUFFIX_RE, "").trim();
    if (next === value) break;
    value = next;
  }
  return value;
};

const tokenize = (value: string): string[] =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

/** Distinctive tokens from the user query (drops LLC / Solutions / etc.). */
export const distinctiveBusinessTokens = (query: string): string[] => {
  const stripped = stripLegalBusinessSuffixes(query);
  return tokenize(stripped).filter((token) => {
    if (token.length < 2) return false;
    if (BUSINESS_STOPWORDS.has(token)) return false;
    return true;
  });
};

export const scoreBusinessSuggestion = (
  query: string,
  suggestionText: string,
): number => {
  const tokens = distinctiveBusinessTokens(query);
  if (tokens.length === 0) return 0;

  const hay = suggestionText.toLowerCase().replace(/[^a-z0-9\s]/g, " ");
  const hayCompact = hay.replace(/\s+/g, "");
  let score = 0;

  for (const token of tokens) {
    if (hay.includes(token)) {
      score += token.length >= 6 ? 4 : 2;
      continue;
    }
    if (hayCompact.includes(token)) {
      score += token.length >= 6 ? 3 : 1;
      continue;
    }
    // Prefix of a word in the suggestion (Blossom → BlossomBreeze)
    const words = hay.split(/\s+/);
    if (words.some((word) => word.startsWith(token) || token.startsWith(word))) {
      score += 1;
    }
  }

  return score;
};

/**
 * Stable sort: higher distinctive-token score first, then original order.
 * If the query has distinctive tokens (e.g. BlossomBreeze) and nothing
 * matches, return [] instead of unrelated "* Solutions LLC" noise.
 */
export const rankBusinessSuggestions = (
  query: string,
  suggestions: GooglePlaceSuggestion[],
): GooglePlaceSuggestion[] => {
  const tokens = distinctiveBusinessTokens(query);
  const indexed = suggestions.map((item, index) => ({
    item,
    index,
    score: scoreBusinessSuggestion(query, item.text),
  }));
  indexed.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.index - b.index;
  });
  if (tokens.length > 0) {
    const matched = indexed.filter((row) => row.score > 0);
    if (matched.length === 0) return [];
    return matched.map((row) => row.item);
  }
  return indexed.map((row) => row.item);
};

export const mergePlaceSuggestions = (
  ...lists: GooglePlaceSuggestion[][]
): GooglePlaceSuggestion[] => {
  const seen = new Set<string>();
  const merged: GooglePlaceSuggestion[] = [];
  for (const list of lists) {
    for (const item of list) {
      if (!item.placeId || !item.text) continue;
      if (seen.has(item.placeId)) continue;
      seen.add(item.placeId);
      merged.push(item);
    }
  }
  return merged;
};
